import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  matchPath,
  Redirect,
  Route,
  Switch,
  useLocation
} from 'react-router-dom';
import AccessManagement from './pages/AccessManagement';
import AccessInternalManagement from './pages/AccessInternalManagement';
import ContractsAndConstructions from './pages/ContractsAndConstructions/contractsAndConstructions.routes';
import DashboardRoutes from './pages/Dashboard';
import DownloadArea from './pages/DownloadArea';
import Financials from './pages/Financials';
import FirstLogin from './pages/FirstLogin';
import { HelpCenter } from './pages/HelpCenter';
import OrdersRoutes from './pages/Orders/orders.routes';
import ProjectsAndMockupsRoutes from './pages/ProjectsMockup/projectsMockup.routes';
import PurchaseRoutes from './pages/Purchase/purchase.routes';
import ReportsDownload from './pages/ReportsDownload';
import Settings from './pages/Settings';
import { ApplicationState } from './store';
import { ECustomerTypeView } from './store/ducks/user/types';
import Loyalty from './pages/Loyalty';
import Header from './pages/Header';
import Notifications from './pages/Notifications';
import { Providers } from './providers/providers';

export enum FaceliftRoutes {
  FACELIFT = '/facelift'
}

export enum ERoutes {
  ORDERS = '/orders',
  FINANCIALS = '/financials',
  PURCHASE = '/purchase',
  SETTINGS = '/settings',
  DASHBOARD = '/dashboard',
  PROJECT_MOCKUP = '/projects-mockup',
  CONTRACTS_AND_CONSTRUCTIONS = '/contracts-constructions',
  DOWNLOAD_AREA = '/download-area',
  HELP_CENTER = '/help-center',

  REPORTS_DOWNLOAD = '/reports/open-orders',
  ACCESS_MANAGEMENT = '/access-management',
  ACCESS_INTERNAL_MANAGEMENT = '/access-internal-management',
  FIRST_LOGIN = '/first-login',
  LOYALTY = '/loyalty',
  NOTIFICATIONS = '/notifications',
  SHOPPING_CART = '/shopping-cart'
}

interface IAccessControllRoutes {
  route: ERoutes;
  component: React.FC;
  hasUserPermission: boolean;
  customerViews: ECustomerTypeView[];
}

const Routes: React.FC = () => {
  const location = useLocation();
  const [canShowHeader, setCanShowHeader] = useState(false);

  const customerViewType = useSelector(
    (state: ApplicationState) => state.user.searchCustomerType
  );
  const userPermissions = useSelector(
    (state: ApplicationState) => state.user.userPermissions
  );
  const isManager = useSelector(
    (state: ApplicationState) => state.user.isManager
  );

  const homePage: ERoutes =
    ![
      ECustomerTypeView.EMITTER,
      ECustomerTypeView.FABRICATION_RECEIVER
    ].includes(customerViewType) || isManager
      ? ERoutes.ORDERS
      : ERoutes.DASHBOARD;

  const accessControllRoutes: IAccessControllRoutes[] = [
    {
      route: ERoutes.ORDERS,
      component: OrdersRoutes,
      hasUserPermission: userPermissions.hasOrderViewAccess || isManager,
      customerViews: [
        ECustomerTypeView.EMITTER,
        ECustomerTypeView.RECEIVER,
        ECustomerTypeView.FABRICATION_RECEIVER
      ]
    },
    {
      route: ERoutes.FINANCIALS,
      component: Financials,
      hasUserPermission: userPermissions.hasFinancialAccess || isManager,
      customerViews: [
        ECustomerTypeView.EMITTER,
        ECustomerTypeView.RECEIVER,
        ECustomerTypeView.FABRICATION_RECEIVER
      ]
    },
    {
      route: ERoutes.PURCHASE,
      component: PurchaseRoutes,
      hasUserPermission: userPermissions.hasOrderInputAccess,
      customerViews: [
        ECustomerTypeView.EMITTER,
        ECustomerTypeView.FABRICATION_RECEIVER
      ]
    },
    {
      route: ERoutes.DASHBOARD,
      component: DashboardRoutes,
      hasUserPermission:
        userPermissions.hasOrderViewAccess ||
        userPermissions.hasFinancialAccess,
      customerViews: [
        ECustomerTypeView.EMITTER,
        ECustomerTypeView.FABRICATION_RECEIVER
      ]
    },
    {
      route: ERoutes.ACCESS_MANAGEMENT,
      component: AccessManagement,
      hasUserPermission: userPermissions.hasAccessManagementAccess,
      customerViews: [
        ECustomerTypeView.EMITTER,
        ECustomerTypeView.RECEIVER,
        ECustomerTypeView.FABRICATION_RECEIVER
      ]
    },
    {
      route: ERoutes.PROJECT_MOCKUP,
      component: ProjectsAndMockupsRoutes,
      hasUserPermission:
        userPermissions.hasOrderInputAccess &&
        userPermissions.hasFabricationAccess,
      customerViews: [
        ECustomerTypeView.EMITTER,
        ECustomerTypeView.FABRICATION_RECEIVER
      ]
    },
    {
      route: ERoutes.CONTRACTS_AND_CONSTRUCTIONS,
      component: ContractsAndConstructions,
      hasUserPermission:
        userPermissions.hasOrderInputAccess &&
        userPermissions.hasFabricationAccess,
      customerViews: [
        ECustomerTypeView.EMITTER,
        ECustomerTypeView.FABRICATION_RECEIVER
      ]
    },
    {
      route: ERoutes.DOWNLOAD_AREA,
      component: DownloadArea,
      hasUserPermission: true,
      customerViews: [
        ECustomerTypeView.EMITTER,
        ECustomerTypeView.RECEIVER,
        ECustomerTypeView.FABRICATION_RECEIVER
      ]
    },
    {
      route: ERoutes.HELP_CENTER,
      component: HelpCenter,
      hasUserPermission: true,
      customerViews: [
        ECustomerTypeView.EMITTER,
        ECustomerTypeView.RECEIVER,
        ECustomerTypeView.FABRICATION_RECEIVER
      ]
    },
    {
      route: ERoutes.LOYALTY,
      component: Loyalty,
      hasUserPermission: userPermissions.hasLoyaltyAccess,
      customerViews: [
        ECustomerTypeView.EMITTER,
        ECustomerTypeView.RECEIVER,
        ECustomerTypeView.FABRICATION_RECEIVER
      ]
    },
    {
      route: ERoutes.NOTIFICATIONS,
      component: Notifications,
      hasUserPermission: userPermissions.hasLoyaltyAccess,
      customerViews: [
        ECustomerTypeView.EMITTER,
        ECustomerTypeView.RECEIVER,
        ECustomerTypeView.FABRICATION_RECEIVER
      ]
    }
  ];

  const CanShowInternalManagementRoute = useCallback(() => {
    // TODO change the permission.
    if (userPermissions.hasAccessManagementAccess) {
      return true;
    } else {
      return false;
    }
  }, [userPermissions]);

  useEffect(() => {
    const reportDownloadMatch = matchPath(location.pathname, {
      path: ERoutes.REPORTS_DOWNLOAD
    });
    const firstLoginMatch = matchPath(location.pathname, {
      path: ERoutes.FIRST_LOGIN
    });
    setCanShowHeader(
      (!reportDownloadMatch || reportDownloadMatch.isExact === false) &&
        (!firstLoginMatch || firstLoginMatch.isExact === false)
    );
  }, [location.pathname]);

  return (
    <Providers>
      {canShowHeader && <Header />}
      <Switch>
        {accessControllRoutes
          .filter(
            (route) =>
              route.hasUserPermission &&
              route.customerViews.includes(customerViewType)
          )
          .map((route) => (
            <Route
              key={route.route}
              path={route.route}
              component={route.component}
            />
          ))}

        <Route path={ERoutes.SETTINGS} component={Settings} />
        <Route path={ERoutes.REPORTS_DOWNLOAD} component={ReportsDownload} />
        <Route path={ERoutes.FIRST_LOGIN} component={FirstLogin} />
        {CanShowInternalManagementRoute() && (
          <Route
            path={ERoutes.ACCESS_INTERNAL_MANAGEMENT}
            component={AccessInternalManagement}
          />
        )}

        <Route path="/" exact>
          <Redirect to={homePage} />
        </Route>

        {/* se não encontrar nenhuma rota */}
        {/* <Route path="*">
                        <Redirect to={homePage} />
                    </Route> */}
      </Switch>
    </Providers>
  );
};

export default Routes;
